import { Button, Card, useToast } from "@design";
import { Download, FileSpreadsheet, FileText, Presentation, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type BrandTemplateResponse, errorMessage } from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Text } from "@/components/Text";
import { formatBytes, formatDate } from "@/i18n/format";
import { TemplateDetails } from "./TemplateDetails";
import {
  checkTemplate,
  downloadTemplate,
  TEMPLATE_EXTENSIONS,
  type TemplateFormat,
  templateErrorCode,
  useRemoveTemplate,
  useTemplateThumbnailUrl,
  useUploadTemplate,
} from "./useBrandTemplates";

/** One format's slot on the Templates tab: empty state with Upload, or the stored template and its actions. */
export function TemplateSlot({
  brandUuid,
  format,
  template,
  canEdit,
}: Readonly<{ brandUuid: string; format: TemplateFormat; template?: BrandTemplateResponse; canEdit: boolean }>) {
  const { t } = useTranslation("brands", { keyPrefix: "templates" });
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { upload, isPending: uploading } = useUploadTemplate(brandUuid);
  const { remove, isPending: removing } = useRemoveTemplate(brandUuid);
  const extensions = TEMPLATE_EXTENSIONS[format].join(", ");
  const Icon = { pptx: Presentation, docx: FileText, xlsx: FileSpreadsheet }[format];

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const problem = checkTemplate(file, format);
    if (problem) {
      toast.error(t(`problems.${problem}`, { extensions }));
      return;
    }
    try {
      await upload(format, file);
      toast.success(t("uploaded"));
    } catch (error) {
      const code = templateErrorCode(error);
      toast.error(code ? t(`errors.${code}`) : errorMessage(error, t("uploadFailed")));
    }
  };

  const onRemove = async () => {
    try {
      await remove(format);
      setConfirming(false);
      toast.success(t("removed"));
    } catch (error) {
      toast.error(errorMessage(error, t("removeFailed")));
    }
  };

  const onDownload = async () => {
    if (!template) return;
    setDownloading(true);
    try {
      await downloadTemplate(brandUuid, template);
    } catch (error) {
      toast.error(errorMessage(error, t("downloadFailed")));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <section aria-label={t(`formats.${format}`)} className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Icon size={18} aria-hidden className="text-content-secondary" />
          <Text as="h3" variant="h3">
            {t(`formats.${format}`)}
          </Text>
        </div>
        <input
          ref={input}
          type="file"
          accept={TEMPLATE_EXTENSIONS[format].join(",")}
          aria-label={t(`uploadInput.${format}`)}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            void onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {template ? (
          <StoredTemplate brandUuid={brandUuid} template={template} />
        ) : (
          <Text variant="body-sm" tone="secondary">
            {t(`empty.${format}`)}
          </Text>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <Button
              variant={template ? "secondary" : "primary"}
              size="sm"
              leadingIcon={<Upload size={14} aria-hidden />}
              loading={uploading}
              onClick={() => input.current?.click()}
            >
              {template ? t("replace") : t("upload")}
            </Button>
          )}
          {template && (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Download size={14} aria-hidden />}
              loading={downloading}
              onClick={() => void onDownload()}
            >
              {t("download")}
            </Button>
          )}
          {canEdit && template && (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Trash2 size={14} aria-hidden />}
              onClick={() => setConfirming(true)}
            >
              {t("remove")}
            </Button>
          )}
        </div>
        {canEdit && (
          <Text variant="caption" tone="tertiary">
            {t("hint", { extensions })}
          </Text>
        )}
      </section>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t("confirmRemoveTitle", { format: t(`formats.${format}`) })}
        body={t("confirmRemoveBody")}
        confirmLabel={t("remove")}
        loading={removing}
        onConfirm={() => void onRemove()}
      />
    </Card>
  );
}

function StoredTemplate({ brandUuid, template }: Readonly<{ brandUuid: string; template: BrandTemplateResponse }>) {
  const { t } = useTranslation("brands", { keyPrefix: "templates" });
  const thumbnail = useTemplateThumbnailUrl(brandUuid, template);
  const filename = template.filename || template.format;
  const meta = {
    size: formatBytes(template.sizeBytes ?? 0),
    date: formatDate(template.updatedAt),
  };
  return (
    <div className="flex flex-col gap-4 md:flex-row">
      {thumbnail && (
        <img
          src={thumbnail}
          alt={t("thumbnailAlt", { filename })}
          className="w-full max-w-60 shrink-0 self-start rounded-lg border border-border-subtle object-contain"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <Text variant="body" className="break-all">
            {filename}
          </Text>
          <Text variant="caption" tone="tertiary">
            {template.uploadedByName ? t("meta", { ...meta, name: template.uploadedByName }) : t("metaNoName", meta)}
          </Text>
        </div>
        {template.details && <TemplateDetails format={template.format} details={template.details} />}
      </div>
    </div>
  );
}
